// ----------------------------------------------------------------------------
// Product Form Web Component: iamCartConfirmation <iam-cart-confirmation>
// Extends: iamModal
// ----------------------------------------------------------------------------
// Handle the display of the "cart confirmation modal"
// - When a product is being added to the cart, modify the AJAX request to include
//   a request to the section rendering API to fetch the `cart-confirmation__item`
//   and `cart-confirmation__summary` sections. This content will be used to
//   populate and then open the modal if the product is successfully added.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:submitting:data'   → Modifies an add-to-cart request to include
//                                     a request for the 'cart-confirmation__item'
//                                     and 'cart-confirmation__summary' sections
// - 'productForm:submitted:success' → Triggered when the product form is successfully
//                                     submitted. It updates the modal contents
//                                     and opens it.
// ----------------------------------------------------------------------------
// Gotchas:
// - On add-to-cart failure, the product form should show an inline error
//   (i.e. it will not open the cart confirmation modal)
// - The Liquid code should include the `cart-confirmation` section to create
//   a hidden shell for the modal that will be dynamically populated
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamModal from 'snippets/_containers/modal/modal';
import { Loadable } from 'scripts/classes/iamota-component-helpers';

// Styles
import './_cart-confirmation.scss';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCartConfirmation extends iamModal {
  constructor() {
    super();
    this.loadable = new Loadable(this);
  }
  connectedCallback() {
    this.cartConfirmationItem = this.querySelector('.cart-confirmation__item');
    this.cartConfirmationContent = this.querySelector('.cart-confirmation__content');
    //this.modal                   = this.querySelector('iamModal#cart_confirmation');

    iamotaEventHandler.subscribe('productForm:submitting:data', this.onProductFormSubmittingData.bind(this), {
      source: "cart-confirmation.js"
    });
    iamotaEventHandler.subscribe('productForm:submitted:success', this.onProductFormSubmitted.bind(this), {
      source: "cart-confirmation.js"
    });
    super.connectedCallback();
  }
  onProductFormSubmittingData(request) {
    var _request$sections;
    this.setLoading(true);
    var sections = new Set((_request$sections = request.sections) === null || _request$sections === void 0 ? void 0 : _request$sections.split(',')); // Existing sections requested
    sections.add('cart-confirmation__item');
    sections.add('cart-confirmation__summary');
    request.sections = Array.from(sections).join(',');
    return request;
  }
  onProductFormSubmitted(_ref) {
    var cart = _ref.cart,
      iamProductForm = _ref.iamProductForm;
    if (cart.key && cart.quantity && cart.sections && cart.sections['cart-confirmation__item'] && cart.sections['cart-confirmation__summary']) {
      var itemKey = cart.key;
      var quantity = iamProductForm.getCurrentQuantity();
      var itemHTML = cart.sections['cart-confirmation__item'];
      var summaryHTML = cart.sections['cart-confirmation__summary'];
      var addToCartButton = iamProductForm.querySelector('iam-add-to-cart-button button');
      this.openCartConfirmation(itemKey, quantity, itemHTML, summaryHTML, addToCartButton);
    } else {
      console.warn('Cannot open Cart Confirmation Modal (missing data in the cart response)', cart.key, cart.quantity, cart.sections, cart);
    }
    this.setLoading(false);
  }
  openCartConfirmation(itemKey, quantity, itemHTML, summaryHTML, triggerElement) {
    // Upate the moal content
    this.cartConfirmationItem.innerHTML = new DOMParser().parseFromString(itemHTML, 'text/html').querySelector("[id=\"cart-confirmation__item--".concat(itemKey, "\"]")).innerHTML;
    var addedQtyElement = this.cartConfirmationItem.querySelector('.added-quantity__value');
    if (addedQtyElement) addedQtyElement.textContent = quantity;
    this.cartConfirmationContent.innerHTML = summaryHTML;

    // Open the modal
    this.openModal(triggerElement);
  }

  /**
   * Expose Loading state methods
   */
  setLoading(isLoading) {
    this.loadable.setLoading(isLoading);
  }
  isLoading() {
    return this.loadable.isLoading();
  }
}
customElements.define('iam-cart-confirmation', iamCartConfirmation);